import merge from 'lodash/merge';
import throttle from 'lodash/throttle';
import Resizable from 'resizable';

import { POPUP_INTERACTION, VIEW_RACE_ANALYSIS } from 'atg/constants/Events';
import { RACE_ANALYSIS_POPUP } from 'atg/constants/Popups';
import observeStore from 'atg/redux/observeStore';
import EventDispatcher from 'atg/utils/EventDispatcher';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { dialogs } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

const $ = window.$;

let active = null;
let $mainScrollContainer = null;

export const create = (api, $scrollContainer, props) => {
  $mainScrollContainer = $scrollContainer;
  $mainScrollContainer.addClass('no-touch-scroll');

  if (active) {
    active.setProps(props);
    active.render();
  } else {
    active = new RaceAnalysisPopup(api, props);
  }
};

export const updateData = props => {
  if (!active) {
    return;
  } else {
    active.setProps(props);
    active.render();
  }
};

export const close = () => active && active.close();

class RaceAnalysisPopup {
  constructor(api, props) {
    this.api = api;
    this.setProps(props);

    this.$outerContainer = StickyTemplate.jq('race-analysis');
    $('#app').append(this.$outerContainer);

    this.$container = this.$outerContainer.find('.race-analysis-container');
    this.$sections = this.$container.find('.sections');
    this.$type = this.$container.find('.type');
    this.$header = this.$container.find('.header');
    this.$startNumber = this.$container.find('.start-number');
    this.$scrollContainer = this.$container.find('.scroll-container');
    this.$contentContainer = this.$container.find('.content-container');
    this.$navigation = this.$container.find('.race-analysis-navigation');
    this.$loader = this.$container.find('.race-analysis-loader');
    this.topHeight = this.$container.find('.race-analysis-top').height();

    this.close = this.close.bind(this);
    this.$container.find('button.close').event('click', this.close);

    EventDispatcher.addListener(POPUP_INTERACTION, this.topPopup.bind(this));
    this.destroyStoreObserver = observeStore(
      getStore(),
      this.onStoreChange,
      dialogs.getDialogs,
    );

    this.resizable = new Resizable(this.$container.get(0), {
      draggable: {
        handle: this.$container.find('.race-analysis-top-bar').get(0),
      },
      handles: 's, se, e, w, sw',
      resize: () => {
        this.isUserResized = true;
        this.resizeScrollContainer();
      },
    });

    this.$container.event('down', this.onPopupInteraction.bind(this));
    EventDispatcher.emit(POPUP_INTERACTION, RACE_ANALYSIS_POPUP);
    this.throttledResize = throttle(this.resize.bind(this), 100);
    $(window).on('resize', this.throttledResize);
    this.resize();

    this.render();
  }

  setProps({ dataSet, raceNumber, numberOfRaces, betType, header }) {
    this.props = merge(this.props, {
      dataSet: dataSet,
      selectedRaceNumber: raceNumber,
      numberOfRaces: numberOfRaces,
      betType: betType,
      header: header,
    });
  }

  onStoreChange = dialogs => {
    if (dialogs.length > 0) {
      this.close();
    }
  };

  resize() {
    if (this.isUserResized) {
      return;
    }

    const ww = window.innerWidth;
    const wh = window.innerHeight;
    const idealW =
      Number.parseInt(this.$contentContainer.css('min-width'), 10) + 20;
    let cw = this.$container.width();
    const ch = this.$container.height();

    const defaultTop = Math.max($('.grid').offset().top - 10, 0);

    const css = {
      top: defaultTop,
    };

    if (cw > ww) {
      css.width = cw = ww;
    } else if (cw < idealW) {
      css.width = cw = Math.min(ww, idealW);
    }

    if (ch + defaultTop > wh) {
      css.height = wh;
      css.top = 0;
    }

    this.$container.css(css);

    const isResizable = cw < ww || ch < wh; // Allow drag and resize unless we take up the whole screen
    if (!isResizable && this.resizable) {
      this.resizable.destroy();
      this.resizable = null;
      this.$outerContainer.css({
        overflow: 'hidden',
      });
    }

    this.resizeScrollContainer();
  }

  render() {
    this.renderNavigation();

    if (!this.props.dataSet) {
      this.renderLoader();
    } else {
      this.hideLoader();
      this.$sections.empty();
      this.$header.empty();
      this.$type.empty();

      this.$type.text(
        `${this.props.betType}–${this.props.selectedRaceNumber + 1}`,
      );
      this.$header.text(this.props.header);

      const sections = [];
      if (this.props.dataSet.hasHeadAnalysis()) {
        sections.push({
          heading: Text.get('race-analysis-head-analysis-heading'),
          body: this.props.dataSet.getHeadAnalysisHTML(),
        });
      }

      if (this.props.dataSet.hasRaceAnalysis()) {
        sections.push({
          heading: Text.get('race-analysis-race-analysis-heading'),
          body: this.props.dataSet.getRaceAnalysisHTML(),
        });
      }

      sections.forEach(section => {
        const $section = StickyTemplate.jq('race-analysis-section', {
          '.heading': section.heading,
          '.body': $('<div/>').html(section.body),
        });
        this.$sections.append($section);
      });

      this.$container.addClass('with-data');
    }
  }

  renderNavigation() {
    this.$navigation.empty();
    for (let i = 0; i < this.props.numberOfRaces; i++) {
      const tpl = StickyTemplate.jq('race-analysis-navigation-item', {
        '%raceNumber%': i,
        '%numberOfRaces%': i + 1,
      });
      if (i === this.props.selectedRaceNumber) {
        tpl.addClass('active');
      }
      tpl.on('click', this.onNavigationClick.bind(this, i));
      tpl.appendTo(this.$navigation);
    }
  }

  onNavigationClick(raceNumber) {
    this.setSelectedRace(raceNumber);
    EventDispatcher.emit(VIEW_RACE_ANALYSIS, raceNumber);

    this.renderLoader();
  }

  setSelectedRace(raceNumber) {
    this.$navigation
      .find(`li[data-id="${this.props.selectedRaceNumber}"]`)
      .removeClass('active');
    this.props.selectedRaceNumber = raceNumber;
    this.$navigation
      .find(`li[data-id="${this.props.selectedRaceNumber}"]`)
      .addClass('active');
  }

  renderLoader() {
    if (this.loaderActive) {
      return;
    }

    this.loaderActive = true;
    const heading = Text.get('main-loader-race-analysis-heading');
    this.$loader.find('h1').html(heading || '&nbsp;');
    this.$loader.fadeIn(200);
  }

  hideLoader() {
    if (!this.loaderActive) {
      return;
    }

    this.loaderActive = false;
    this.$loader.fadeOut(200);
  }

  close() {
    active = null;
    EventDispatcher.removeListener(POPUP_INTERACTION, this.topPopup);
    this.destroyStoreObserver();
    this.throttledResize.cancel();
    $(window).unevent('resize', this.throttledResize);
    if (this.resizable) {
      this.resizable.destroy();
    }
    $mainScrollContainer.removeClass('no-touch-scroll');
    this.$outerContainer.remove();
  }

  resizeScrollContainer() {
    this.$scrollContainer.height(this.$container.height() - this.topHeight);
  }

  topPopup(popup) {
    if (popup === RACE_ANALYSIS_POPUP) {
      this.$outerContainer.addClass('top-popup');
    } else {
      this.$outerContainer.removeClass('top-popup');
    }
  }

  onPopupInteraction() {
    if (!this.$outerContainer.hasClass('top-popup')) {
      EventDispatcher.emit(POPUP_INTERACTION, RACE_ANALYSIS_POPUP);
    }
  }
}
